import ActionTypes, { SaveMode } from './constants';
import { makeSelectSelectedIdCabinet } from './../Configurator/selectors/furniture';
import {
  displayFurniture,
  updateCabinetSuccess,
} from './../Configurator/actions/furniture';
import { call, delay, put, select, take } from '@redux-saga/core/effects';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import {
  CABINET_URL,
  FURNITURE_URL,
} from 'containers/Configurator/constants/furniture';
import { requestPostJson } from 'utils/requests';
import { Furniture } from 'lib/modelBuilder/entities/Furniture';
import {
  makeSelectCabinetImages,
  makeSelectFurniture,
} from 'containers/Configurator/selectors/furniture';
import {
  saveFurnitureFailureAction,
  saveFurnitureSuccessAction,
} from 'containers/UserActions/actions';
import { toast } from 'react-toastify';
import properties from '../../helpers/config';
import { getImageCabinet } from '../Configurator/sagas/furniture';
import { CabinetJson, FurnitureJson } from '../../lib/modelBuilder/Data';
import ActionTypesViewer from 'containers/Viewer/constants';

const sortCabinetJson = (cabinet: Cabinet | undefined): string => {
  // Recreate the cabinet with form to have the same order of JSON
  // entries in order to compute the hash and check unicity between two cabinet
  if (cabinet) {
    const cabinetTmp = JSON.stringify(cabinet, Cabinet.replacer);
    const cabinetJSon = JSON.parse(cabinetTmp);
    const cabinetNormalized = Cabinet.fromModel(cabinetJSon as CabinetJson);
    return JSON.stringify(cabinetNormalized, Cabinet.replacer);
  } else return '';
};

const sortFurnitureJson = (furniture: Furniture | undefined): string => {
  // Recreate the furniture with form to have the same order of JSON
  // entries in order to compute the hash and check unicity between two cabinet
  if (furniture) {
    const furnitureTmp = JSON.stringify(furniture, Furniture.replacer);
    const furnitureJson = JSON.parse(furnitureTmp);
    const furnitureNormalized = Furniture.fromModel(
      furnitureJson as FurnitureJson,
    );
    furnitureNormalized.image = furniture.image;
    return JSON.stringify(furnitureNormalized, Furniture.replacer);
  } else return '';
};

export default function* saveSaga(): any {
  while (true) {
    const { payload } = yield take(ActionTypes.SAVE_FURNITURE_REQUEST);
    const { saveMode } = payload;

    const cart =
      saveMode === SaveMode.AddToCart ||
      saveMode === SaveMode.AddToCurrentOrder;

    const furniture: Furniture | undefined = yield select(
      makeSelectFurniture(),
    );

    let data = undefined;
    let url = `${CABINET_URL}/`;
    if (furniture) {
      if (furniture.quantity === 1) {
        if (furniture.first) {
          furniture.first.image = yield call(getImageCabinet);
        }
        data = sortCabinetJson(furniture.first);
      } else {
        const selectedCabinetId = yield select(makeSelectSelectedIdCabinet());
        if (selectedCabinetId !== null) {
          yield put(displayFurniture());

          yield take(ActionTypesViewer.FURNITURE_DISPLAYED);
          // Add max animation delay
          yield delay(500);
        }

        furniture.image = yield call(getImageCabinet);

        const cabinetImages: Map<string, string> = yield select(
          makeSelectCabinetImages(),
        );

        for (let i = 0; i < furniture.cabinets.length; i++) {
          if (cabinetImages.has(furniture.cabinets[i].cabinet.id)) {
          }
          furniture.cabinets[i].cabinet.image = cabinetImages.get(
            furniture.cabinets[i].cabinet.id,
          );
        }

        data = sortFurnitureJson(furniture);

        url = `${FURNITURE_URL}/`;
      }

      try {
        const response = yield call(requestPostJson, url, data);
        const { id, prestashopId } = response;

        let savedFurniture;
        if (furniture.quantity === 1) {
          savedFurniture = furniture.clone();
          savedFurniture.cabinets[0].cabinet = Cabinet.fromModel(
            response as CabinetJson,
          );
          savedFurniture.cabinets[0].cabinet.id =
            furniture.cabinets[0].cabinet.id;
          savedFurniture.cabinets[0].cabinet.prestashopId = prestashopId;
          yield put(
            updateCabinetSuccess(
              savedFurniture,
              furniture.cabinets[0].cabinet.id,
              false,
              undefined,
              false,
            ),
          );
        } else {
          savedFurniture = Furniture.fromModel(response as FurnitureJson);
          savedFurniture.prestashopId = prestashopId;
          yield put(
            updateCabinetSuccess(savedFurniture, null, false, undefined, false),
          );
        }

        if (cart && !prestashopId) {
          throw new Error(
            "We don't have the prestashopId after adding the product to Kewapp",
          );
        }

        if (saveMode === SaveMode.AddToCart) {
          if (properties.addToCartCallback) {
            properties.addToCartCallback(prestashopId);
          }
        } else if (saveMode === SaveMode.AddToCurrentOrder) {
          if (properties.addToCurrentOrderCallback) {
            properties.addToCurrentOrderCallback(
              prestashopId,
              savedFurniture.getSalesPriceTaxInclude(
                1,
                properties.resellerRate ? properties.resellerRate : 1,
              ),
              savedFurniture.getSalesPriceTaxInclude(
                properties.vat ? properties.vat : 1,
                properties.resellerRate ? properties.resellerRate : 1,
              ),
            );
          }
        } else if (
          saveMode !== SaveMode.Print &&
          saveMode !== SaveMode.SaveForLater &&
          properties.saveCallback
        ) {
          properties.saveCallback(prestashopId);
        } else if (
          saveMode === SaveMode.Print ||
          saveMode === SaveMode.SaveForLater
        ) {
          window.onbeforeunload = null;
        }

        yield put(saveFurnitureSuccessAction(saveMode));

        // yield put(loadFurnitureAction(`${id}`));
      } catch (error) {
        if (cart && properties.addToCartCallback) {
          // If error, inform kewnet
          properties.addToCartCallback(-1);
        } else if (properties.saveCallback) {
          properties.saveCallback(-1);
        }
        yield put(saveFurnitureFailureAction(error));
        toast.error(
          `An error occurs when saving the furniture; ${error.statusText}`,
          {
            autoClose: 5000,
            pauseOnHover: true,
          },
        );
      }
    } else {
      yield put(saveFurnitureFailureAction('No furniture to save'));
    }
  }
}
